import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { PRESET_AVATARS } from "../lib/gameLogic";
import { Sparkles, Check, ChevronRight } from "lucide-react";
import { sfx, primeAudio } from "../lib/sounds";

export default function Onboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [avatarId, setAvatarId] = useState(PRESET_AVATARS[0].id);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-50" />
      <div className="absolute -top-32 left-1/3 h-96 w-96 bg-purple-600/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 right-1/4 h-96 w-96 bg-blue-600/20 rounded-full blur-3xl" />

      <div className="relative w-full max-w-2xl border border-purple-500/40 bg-[#0A0D14]/80 backdrop-blur-xl p-7 md:p-10 scanline">
        <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.4em] text-blue-300">
          <Sparkles className="h-3 w-3 animate-flicker" />
          The System
          <Sparkles className="h-3 w-3 animate-flicker" />
        </div>

        {step === 0 && (
          <div className="mt-4 text-center animate-rise">
            <h1
              className="font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-white text-glow-purple"
              data-testid="onboarding-title"
            >
              You have been chosen
            </h1>
            <p className="mt-3 text-slate-400 max-w-md mx-auto leading-relaxed">
              A dungeon has opened within you. Train daily, slay your quests,
              and ascend the ranks from{" "}
              <span className="text-slate-300">E-Rank</span> to{" "}
              <span className="text-amber-300">SSS-Rank</span>.
            </p>
            <div className="mt-7">
              <Button
                onClick={() => {
                  primeAudio();
                  sfx.rankUp();
                  setStep(1);
                }}
                className="rounded-none bg-purple-600 hover:bg-purple-500 px-8 py-6 text-base uppercase tracking-[0.25em] font-bold font-display gap-2 animate-pulse-glow"
                data-testid="onboarding-arise-button"
              >
                Arise <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="mt-4 animate-rise">
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl text-white text-center">
              State Your Name, Hunter
            </h2>
            <div className="mt-6 max-w-sm mx-auto">
              <Label className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                Codename
              </Label>
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jin-Woo"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && name.trim()) setStep(2);
                }}
                className="mt-1 bg-black/50 border-white/10 rounded-none focus-visible:ring-purple-500 font-display text-lg"
                data-testid="onboarding-name-input"
              />
            </div>
            <div className="mt-7 flex items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(0)}
                className="rounded-none border-white/20 bg-transparent text-slate-300 hover:bg-white/5"
              >
                Back
              </Button>
              <Button
                disabled={!name.trim()}
                onClick={() => {
                  sfx.click();
                  setStep(2);
                }}
                className="rounded-none bg-purple-600 hover:bg-purple-500 gap-2 uppercase tracking-widest font-display"
                data-testid="onboarding-next-button"
              >
                Continue <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-4 animate-rise">
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl md:text-3xl text-white text-center">
              Select Your Vessel
            </h2>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRESET_AVATARS.map((a) => {
                const active = a.id === avatarId;
                return (
                  <button
                    key={a.id}
                    onClick={() => {
                      sfx.click();
                      setAvatarId(a.id);
                    }}
                    className={`relative border-2 ${
                      active
                        ? "border-purple-400 ring-glow-purple"
                        : "border-white/10 hover:border-blue-400"
                    } transition`}
                    data-testid={`onboarding-avatar-${a.id}`}
                  >
                    <img
                      src={a.url}
                      alt={a.name}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black to-transparent p-2 text-center">
                      <div className="font-display uppercase tracking-wider text-xs">
                        {a.name}
                      </div>
                    </div>
                    {active && (
                      <div className="absolute top-1 right-1 bg-purple-500 p-0.5">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-7 flex items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="rounded-none border-white/20 bg-transparent text-slate-300 hover:bg-white/5"
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  sfx.levelUp();
                  onComplete(name.trim(), avatarId);
                }}
                className="rounded-none bg-purple-600 hover:bg-purple-500 px-7 uppercase tracking-widest font-display animate-pulse-glow"
                data-testid="onboarding-enter-button"
              >
                Enter The System
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
